/**
 * iter87.199.26 — Share Season Recap.
 *
 * Card + modal on Tournament Settings that lets the rivalry admin
 * email the season recap link to selected crewmates (with a real
 * `email` on file). Only surfaces when the tournament is
 * marked complete (`status === "completed"`). Select-all default
 * + individual toggles; confirm before sending. Rate-limited
 * server-side at 15 min per tournament.
 */
import { useEffect, useState } from "react";
import { Mail, X, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BentoModal } from "@/components/onui/BentoMotion";
import { API } from "@/lib/api";

export default function ShareRecapCard({ tournament }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [selected, setSelected] = useState({});

    // iter87.199.124 — Canonical "is complete" check across the app is
    // now `!!completion_authorized_by` (see `common.tournament_is_locked`
    // in the backend). Only the two manual completion actions stamp
    // this field, so a stale/zombie process writing `status="completed"`
    // directly can no longer flip this card into view.
    const isFinished = !!tournament?.completion_authorized_by;

    useEffect(() => {
        if (!open || !tournament?.id) return;
        setLoading(true);
        axios.get(`${API}/tournaments/${tournament.id}/recap-recipients`, { withCredentials: true })
            .then((res) => {
                const items = res?.data?.items || [];
                setRecipients(items);
                // Default everyone selected.
                const next = {};
                items.forEach((r) => { next[r.participant_id] = true; });
                setSelected(next);
            })
            .catch((e) => {
                toast.error(e?.response?.data?.detail || "Couldn't load recipients");
                setOpen(false);
            })
            .finally(() => setLoading(false));
    }, [open, tournament?.id]);

    const selectedCount = Object.values(selected).filter(Boolean).length;
    const allChecked = recipients.length > 0 && selectedCount === recipients.length;
    const noneChecked = selectedCount === 0;

    const toggleAll = () => {
        const next = {};
        if (!allChecked) recipients.forEach((r) => { next[r.participant_id] = true; });
        setSelected(next);
    };

    const send = async () => {
        const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
        if (!ids.length) return;
        setSending(true);
        try {
            const res = await axios.post(
                `${API}/tournaments/${tournament.id}/email-recap`,
                { participant_ids: ids },
                { withCredentials: true },
            );
            const resp = res?.data;
            if (resp?.rate_limited) {
                const at = resp.next_available_at ? new Date(resp.next_available_at).toLocaleTimeString() : "soon";
                toast.warning(`Rate-limited — try again after ${at}.`);
            } else {
                toast.success(`Recap emailed to ${resp?.sent ?? 0} crewmate${(resp?.sent ?? 0) === 1 ? "" : "s"}.`);
                setOpen(false);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <div className="brutal-card p-5 space-y-3" data-testid="share-recap-card">
                <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-cyber" />
                    <h2 className="font-display font-extrabold text-xl uppercase">Share Season Recap</h2>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                    Email the season recap link to your crew. Pick who receives it — everyone with an email on file is checked by default.
                </p>
                {!isFinished && (
                    <p className="text-[11px] text-zinc-500 italic" data-testid="share-recap-not-finished-note">
                        Available after the tournament is marked complete.
                    </p>
                )}
                <button
                    type="button"
                    data-testid="share-recap-open-btn"
                    disabled={!isFinished}
                    onClick={() => setOpen(true)}
                    className="brutal-btn text-xs py-2 px-3 bg-cyber text-black border-black shadow-brutal disabled:opacity-40 disabled:pointer-events-none"
                >
                    <Mail className="w-3.5 h-3.5" />
                    Email Recap to Crew
                </button>
            </div>

            <BentoModal
                open={open}
                onClose={() => !sending && setOpen(false)}
                testId="share-recap-modal"
                backdropClassName="items-end sm:items-center justify-center sm:p-4"
                panelClassName="brutal-card w-full sm:max-w-md max-h-[85dvh] overflow-hidden flex flex-col bg-ink border-2 border-cyber shadow-2xl"
            >
                <div className="sticky top-0 z-10 bg-ink border-b-2 border-zinc-800 px-4 py-3 flex items-center justify-between gap-2">
                    <h2 className="font-display font-extrabold text-base uppercase tracking-wide truncate">Share Recap</h2>
                    <button onClick={() => setOpen(false)} disabled={sending} className="p-1.5 text-zinc-500 hover:text-white shrink-0" aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto space-y-2 flex-1">
                    {loading && <p className="text-xs text-zinc-500">Loading…</p>}
                    {!loading && recipients.length === 0 && (
                        <p className="text-xs text-zinc-400 italic">No crewmates have an email on file. Add emails on the Players tile.</p>
                    )}
                    {!loading && recipients.length > 0 && (
                        <>
                            <label className="flex items-center gap-2 py-1.5 border-b border-zinc-800 text-xs uppercase tracking-widest font-bold text-cyber cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="share-recap-select-all"
                                    checked={allChecked}
                                    onChange={toggleAll}
                                    className="accent-cyber w-4 h-4"
                                />
                                {allChecked ? "Deselect all" : "Select all"} · {selectedCount} of {recipients.length}
                            </label>
                            {recipients.map((r) => (
                                <label key={r.participant_id} className="flex items-center gap-2 py-1.5 text-[13px] text-zinc-200 cursor-pointer hover:bg-zinc-900/60 px-1 rounded">
                                    <input
                                        type="checkbox"
                                        data-testid={`share-recap-toggle-${r.participant_id}`}
                                        checked={!!selected[r.participant_id]}
                                        onChange={() => setSelected({ ...selected, [r.participant_id]: !selected[r.participant_id] })}
                                        className="accent-cyber w-4 h-4"
                                    />
                                    <span className="flex-1 min-w-0">
                                        <span className="font-bold truncate block">{r.username}</span>
                                        <span className="text-[10px] text-zinc-500 truncate block">{r.email}</span>
                                    </span>
                                    {r.is_admin && (
                                        <span className="text-[9px] uppercase font-bold text-cyber border border-cyber/40 px-1.5 py-0.5 rounded">Admin</span>
                                    )}
                                </label>
                            ))}
                        </>
                    )}
                </div>

                <div className="border-t-2 border-zinc-800 p-3 flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        disabled={sending}
                        className="brutal-btn text-xs py-2 px-3 flex-1 bg-transparent text-zinc-300 border-zinc-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="share-recap-send-btn"
                        onClick={send}
                        disabled={sending || noneChecked || recipients.length === 0}
                        className="brutal-btn text-xs py-2 px-3 flex-1 bg-cyber text-black border-black shadow-brutal disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Send className="w-3.5 h-3.5" />
                        {sending ? "Sending…" : `Send (${selectedCount})`}
                    </button>
                </div>
            </BentoModal>
        </>
    );
}
